import React, {useEffect, useState} from "react";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet";
import { Box, Tabs as MuiTabs, Tab as MuiTab, Typography as MuiTypography } from "@material-ui/core";
import { spacing } from "@material-ui/system";
import {useHistory, useLocation} from "react-router-dom";

import { useAuth } from "../../../context/auth";
import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import {makeStyles} from "@material-ui/core/styles";
import {useGlobalState} from "../../../context/global_state";
import {getSchoolScoreTabs} from "./components/SchoolScoreTabs.def";
import {useSchoolRubricData} from "./hooks/useSchoolRubricData";
import {getSchoolPerformanceService, getSchools, getScoresOverviewService} from "../../../services/school.service";
import {GET_APP_LOCALIZE_TEXT} from "../../../utils/dq_lc_service/lc_service";

const Tabs = styled(MuiTabs)(spacing);
const Tab = styled(MuiTab)(spacing);
const Typography = styled(MuiTypography)(spacing);

function TabPanel({ children, value, index }) {
    return (
        <div role="tabpanel" hidden={value !== index} aria-hidden={value !== index}>
            <Box py={3}>{children}</Box>
        </div>
    );
}

const useStyles = makeStyles(theme => ({
    root:{
        backgroundColor: "grey",
        color: theme.palette.secondary.contrastText,
        margin: "0px 1px 0px 1px",
        padding: "0px 0px 0px 0px",
        minWidth: "auto",
        minInlineSize: "auto",

        "&.Mui-selected": {
            backgroundColor: theme.sidebar.background,
            color: theme.palette.secondary.contrastText,

        }
    },

    indicator: {
        backgroundColor: "primary",
        height: "100%",
        opacity: "0%",
        width: "100%",
    },

    link: {

    }
}));

function SchoolScore() {
    const { authUser } = useAuth();
    const location = useLocation();
    const pageLc = location.pathname.split('/')[1];
    const { settings } = useGlobalState();
    const selectedReport = settings.selected_report;
    const module_code = settings.selected_report;
    const history = useHistory();
    const { pathname } = location;
    const basePath = `/${pageLc}/school/school_score`;
    const classes = useStyles();
    const [school, setSchool] = useState('');
    const [distributionData, setDistributionData] = useState({});
    const {
        loading,
        processedRubricSchool,
        assessIndicators
    } = useSchoolRubricData(authUser.org_code, selectedReport, school, authUser.org_obj.org_country, pageLc);
    const [selectedCategory, setSelectedCategory] = useState( "");
    const [monthRange, setMonthRange] = useState([null, null]);
    const [scoresOverviewNationalScores, setScoresOverviewNationalScores] = useState([]);
    const tabDefs = getSchoolScoreTabs(
        processedRubricSchool,
        assessIndicators,
        selectedCategory,
        setSelectedCategory,
        monthRange,
        setMonthRange,
        pageLc,
        scoresOverviewNationalScores,
        authUser?.org_obj?.org_country,
        distributionData
    );
    const currentTabIdx = tabDefs.findIndex(tab =>
        pathname.startsWith(`${basePath}/${tab.path}`)
    );

    const toYmd = (value) => {
        if (!value) return null;
        const d = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(d.getTime())) return null;

        const y = d.getUTCFullYear();
        const m = String(d.getUTCMonth() + 1).padStart(2, "0");
        const day = String(d.getUTCDate()).padStart(2, "0");

        return `${y}-${m}-${day}`;
    };
    useEffect(() => {
        async function fetchScoresOverviewForIndicator() {
            const indicator_code = selectedCategory;
            if (!authUser?.org_code || !module_code || !authUser?.org_obj?.org_country) return;
            if (!indicator_code) {
                setScoresOverviewNationalScores([]);
                return;
            }
            try {
                const res = await getScoresOverviewService(
                    authUser.org_code,
                    module_code,
                    authUser.org_obj.org_country,
                    [indicator_code]
                );
                const data = res?.data;
                setScoresOverviewNationalScores(Array.isArray(data?.national_scores) ? data.national_scores : []);
            } catch (e) {
                console.log("Error fetching scores overview for indicator:", e);
                setScoresOverviewNationalScores([]);
            }
        }
        fetchScoresOverviewForIndicator();
    }, [authUser?.org_code, authUser?.org_obj?.org_country, module_code, selectedCategory]);

    useEffect(() => {
        async function fetchPerformance() {
            if (!authUser?.org_code || !module_code) return;
            if (!monthRange?.[0] || !monthRange?.[1]) {
                console.log("⏸ Skipping getSchoolPerformanceService — dates not ready", monthRange);
                return;
            }

            const start_date = toYmd(monthRange[0]);
            const end_date = toYmd(monthRange[1]);
            const activeTabPath = pathname.startsWith(`${basePath}/`)
                ? pathname.slice(`${basePath}/`.length).split("/")[0]
                : undefined;
            // Only fetch distribution data for distribution tabs
            if (activeTabPath !== "by_age" && activeTabPath !== "by_level" && activeTabPath !== "by_class") {
                setDistributionData({});
                return;
            }

            const dist_keys =
                activeTabPath === "by_age"
                    ? ["age"]
                    : activeTabPath === "by_level"
                        ? ["section_level"]
                        : ["section_code"];

            const payload = {
                org_code: authUser.org_code,
                module_code,
                start_date,
                end_date,
                dist_keys,
            };
            console.log("📤 Sending payload to getSchoolPerformanceService:", payload);
            try {
                const res = await getSchoolPerformanceService(
                    authUser.org_code,
                    module_code,
                    start_date,
                    end_date,
                    dist_keys
                );
                console.log("getSchoolPerformanceService response:", res?.data);
                const schoolPerformance = Array.isArray(res?.data?.school_performance)
                    ? res.data.school_performance
                    : [];

                const pickGroupDistributions = (item) => {
                    if (activeTabPath === "by_age") {
                        return (
                            item?.distribution_age ||
                            {}
                        );
                    }
                    if (activeTabPath === "by_level") {
                        return (
                            item?.distribution_section_level ||
                            {}
                        );
                    }
                    // by_class
                    return (
                        item?.distribution_section_code ||
                        {}
                    );
                };

                const byGroupData = schoolPerformance.reduce((acc, item) => {
                    const indicatorCode = item?.assess_code;
                    if (!indicatorCode) return acc;

                    const groupDistributions = pickGroupDistributions(item);

                    if (Array.isArray(groupDistributions)) {
                        groupDistributions.forEach((entry) => {
                            const groupKey =
                                entry?.age ??
                                entry?.section_level ??
                                entry?.section_code ??
                                entry?.level ??
                                entry?.class ??
                                entry?.group ??
                                entry?.g;
                            const dist = entry?.distribution ?? entry?.dist ?? entry;
                            if (groupKey == null || dist == null) return;
                            const group = String(groupKey);
                            if (!acc[group]) acc[group] = {};
                            acc[group][indicatorCode] = { school: dist?.school ?? dist };
                        });
                        return acc;
                    }

                    Object.entries(groupDistributions || {}).forEach(([groupKey, dist]) => {
                        if (groupKey == null || dist == null) return;
                        const group = String(groupKey);
                        if (!acc[group]) acc[group] = {};
                        acc[group][indicatorCode] = { school: dist?.school ?? dist };
                    });

                    return acc;
                }, {});

                setDistributionData(byGroupData);
                console.log("FINAL distributionData", activeTabPath, byGroupData);
            } catch (e) {
                console.error("Error calling getSchoolPerformanceService:", e, payload);
            }
        }

        fetchPerformance();
    }, [authUser?.org_code, module_code, monthRange, pathname, basePath]);

    const handleTabChange = (e, idx) => {
        history.push(`${basePath}/${tabDefs[idx].path}`);
    };

    useEffect(() => {
        if (currentTabIdx === -1) {
            history.replace(`${basePath}/overall`);
        }
    }, [pathname]);

    useEffect(() => {
        const loadSchoolList = async (page = 0, rowsPerPage = 10, order = "asc", orderBy = undefined) => {
            try {
                const result = await getSchools(authUser.org_code, selectedReport, page, rowsPerPage, order, orderBy);
                if (result.data) {
                    console.log('Result School List --> ', result.data);
                    setSchool(result.data.data[0]?.school_name);
                }
            } catch (e) {
                console.log("Some error occurred while fetching progress data");
            }
        };
        // Call with default params or adjust as needed
        loadSchoolList();
    }, []);

    return (
        <React.Fragment>
            <Helmet title={GET_APP_LOCALIZE_TEXT(pageLc, "school_score__title") || "School Score"} />
            <Box width="100%" py={4}>
                <Tabs
                    value={currentTabIdx === -1 ? 0 : currentTabIdx}
                    onChange={handleTabChange}
                    TabIndicatorProps={{ className: classes.indicator }}
                    style={{paddingLeft: "30px", paddingRight: "30px"}}
                    centered
                    variant="fullWidth"
                    scrollButtons="auto"
                    aria-label="styled tabs example"
                >
                    {tabDefs.map(tabDef => (
                        <Tab key={tabDef.key} label={tabDef.label} classes={{root: classes.root}}  style={{ fontWeight: "bold", fontSize: 16, minWidth: 200 }}/>
                    ))}
                </Tabs>
                <Box mt={3}>
                    {tabDefs.map((tabDef, idx) => (
                        <TabPanel key={tabDef.key} value={currentTabIdx} index={idx}>
                            {tabDef.component}
                        </TabPanel>
                    ))}
                </Box>
            </Box>
            <LoaderWithBackDrop loading={loading} />
        </React.Fragment>
    );
}

export default SchoolScore;